import React, { JSX, useEffect, useState } from 'react';
import { StatusColour } from '../../fundamentals/colours/variants';
import { ICtaButtonBarProps } from '../../components/CtaButtonBar/CtaButtonBar';
import { IAllowBodyStyleOverrideProps } from '../../common/interfaces';

export enum AlertGroupName {
  Alert = `Alert`,
  MicroAlert = `MicroAlert`,
  Notification = `Notification`,
  Toast = `Toast`
}

/* *
 * id: unique id to remove alerts later.
 * variant: alert variant.
 * title: alert title.
 * */
export interface IAlertState extends Pick<ICtaButtonBarProps, 'ctaButtons' | 'ctaFullOnMobile'>, IAllowBodyStyleOverrideProps {
  id?: string;
  variant?: StatusColour;
  children: JSX.Element | React.ReactNode;
  title?: string;
  closable?: boolean;
  isProcessing?: boolean;
  persist?: boolean;
  light?: boolean;
  onClose?: (e?: any) => void;
  severityIconOutline?: boolean;
  micro?: boolean;
  processingLabel?: string;
  onUndo?: () => void;
  undoLabel?: string;
}

export type AlertGroupState = {
  [key in AlertGroupName]: IAlertState[];
};

type AlertStateListener = () => void;
type CreateAlertStateResult = (key: AlertGroupName) => {
  state: IAlertState[];
  addAlertState: (key: AlertGroupName, next: IAlertState) => void;
  removeAlertState: (key: AlertGroupName, id: string) => void;
  updateAlertState: (key: AlertGroupName, id: string, partial: Partial<IAlertState>) => void;
  clearAlertState: (key: AlertGroupName) => void;
};

const initialAlertState: AlertGroupState = {
  [AlertGroupName.Alert]: [],
  [AlertGroupName.MicroAlert]: [],
  [AlertGroupName.Notification]: [],
  [AlertGroupName.Toast]: []
};

/* *
 * Initialize app alert state.
 * Store all the subscribers by keys, and call them on state change.
 * Return useAlertState as a global hook for further access within an app.
 * { state, addAlertState, removeAlertState, updateAlertState, clearAlertState } options are available as return options
 * */
const createAlertState = (initialState: AlertGroupState): CreateAlertStateResult => {
  let alertState = initialState;
  const stateListeners = Object.fromEntries(Object.keys(initialState).map((key) => [key, new Set<AlertStateListener>()]));

  const addAlertState = (key: AlertGroupName, next: IAlertState): void => {
    alertState = { ...alertState, [key]: [...alertState[key], next] };
    stateListeners[key].forEach((listener: AlertStateListener) => listener());
  };

  const removeAlertState = (key: AlertGroupName, id: string): void => {
    alertState = { ...alertState, [key]: alertState[key].filter((item) => item.id !== id) };
    stateListeners[key].forEach((listener: AlertStateListener) => listener());
  };

  const updateAlertState = (key: AlertGroupName, id: string, partial: Partial<IAlertState>): void => {
    const updatedState = alertState[key].map((itemState: IAlertState) => (itemState.id === id ? { ...itemState, ...partial } : itemState));
    alertState = {
      ...alertState,
      [key]: [...updatedState]
    };
    stateListeners[key].forEach((listener: AlertStateListener) => listener());
  };

  const clearAlertState = (key: AlertGroupName): void => {
    alertState = { ...alertState, [key]: [] };
    stateListeners[key].forEach((listener: AlertStateListener) => listener());
  };

  return (key: AlertGroupName) => {
    const [state, setState] = useState(alertState[key]);
    useEffect(() => {
      const stateListener: AlertStateListener = (): void => setState(alertState[key]);
      stateListeners[key].add(stateListener);
      return () => {
        stateListeners[key].delete(stateListener);
      };
    }, []);
    return { state, addAlertState, removeAlertState, updateAlertState, clearAlertState };
  };
};

/* *
 * Initialize alert state once, make it available to a consumer through useAlertState.
 * Usage:
 * const { state, addAlertState, removeAlertState, updateAlertState, clearAlertState } = useAlertState(GroupName);.
 * */
export const useAlertState = createAlertState(initialAlertState);
